import { useState } from 'react';
import { Button, Grid, IconButton, Paper, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { rolesApiKeyStore } from '../../../api/masters/roles-api-key-store';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { Role } from '../../../models/masters/role';
import { roleColumns } from './role-grid-columns';
import { SaveHookFormDialog } from '../../../components/save-hook-form.dialog';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { Permission } from '../../../models/masters/permission';
import { RoleForm } from './role.form';
import { SiTrackPage } from '../../../components/si-track-page';
import { UserPermissionName } from '../../../models/user-permission-name';
import { GuardedComponent } from '../../../components/guarded-component';
import DeleteIcon from '@mui/icons-material/Delete';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useDeleteData } from '../../../hooks/useDeleteData';
import * as Yup from 'yup';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { useAuthorize } from '../../../hooks/use-authorize';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '20px' },
};

interface CellProps {
  role: any;
}
interface CustomGridCellProps {
  field: string;
}

const roleFormSchema = Yup.object().shape({
  roleName: Yup.string().required('Role name is required'),
  roleCode: Yup.string().required('Role code is required'),
});

export const RolesPage = () => {
  const [editItem, setEditItem] = useState<any>();
  const [deleteItem, setDeleteItem] = useState<Partial<Role>>();
  const { data: permissionList, isLoading } = useLoadData<ApiResponse<Permission[]>>(rolesApiKeyStore.getPermissions());
  const { data, isLoading: isLoadingRole, refetch } = useLoadData<ApiResponse<Role[]>>(rolesApiKeyStore.getRoles());
  const {
    isLoading: isloadingDelete,
    isError,
    error,
    mutateAsync,
  } = useDeleteData(rolesApiKeyStore.deleteRole(deleteItem?.roleID || 0));
  const [isOpen, setIsOpen] = useState(false);
  const { showSnackbar } = useSnackbar();

  const submitHanlder = async () => {
    await mutateAsync();
    refetch();
    closeDialog();
  };

  const openDialog = () => {
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
  };
  const handleCreate = () => {
    setEditItem({ roleCode: '' });
  };

  const handleEdit = (role: Role) => {
    setEditItem({
      ...role,
      rolePermissions: role.rolePermissions.map((p) => p.permissionID),
    });
  };
  const handleDelete = (role: Role) => {
    if (!role?.isSystemDefined) {
      openDialog();
      setDeleteItem(role);
    }
  };

  const handleEditClose = () => {
    setEditItem(undefined);
  };
  const handleDeleteClose = () => {
    setDeleteItem(undefined);
  };

  const formatSubmitdata = (data: Role) => {
    if (data.rolePermissions?.length === 0 || !data.rolePermissions) {
      showSnackbar('No permission is selected', 'error');
      return null;
    } else {
      return { ...data, rolePermissions: data.rolePermissions?.map((p) => ({ permissionID: p })) || [] };
    }
  };

  const OptionCell = ({ role }: CellProps) => {
    return (
      <td>
        <Grid container direction="row" justifyContent="flex-start" alignItems="center">
          <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_ROLE]}>
            <IconButton
              //  disabled={!bundleService?.isEditable}
              aria-label="edit"
              onClick={() => handleEdit(role)}
            >
              <Tooltip title={<Typography variant="body1">Edit</Typography>}>
                <EditIcon className="btnEdit" />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
          <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_ROLE]}>
            <IconButton
              aria-label="delete"
              onClick={() => handleDelete(role)}
            // disabled={role?.isSystemDefined}
            >
              <Tooltip
                title={
                  <Typography variant="body1">
                    {role?.isSystemDefined ? "Can't delete system defined role" : 'Delete'}
                  </Typography>
                }
              >
                <DeleteIcon className="btnDelete" style={{ color: role?.isSystemDefined ? 'lightgrey' : '#ff8a8a ' }} />
              </Tooltip>
            </IconButton>
          </GuardedComponent>
        </Grid>
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'option') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <OptionCell role={dataItem.dataItem} />;
      };
    }
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <SiTrackPage
      title="Role"
      actionButton={
        <GuardedComponent requiredPermissions={[UserPermissionName.ADD_ROLE]}>
          <Button
            size="small"
            variant="contained"
            disableElevation={true}
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Add Role
          </Button>
        </GuardedComponent>
      }
    >
      <Paper elevation={0} style={styling.paperStyle}>
        <SiTrackGrid
          fileTitle='role'
          dataItemKey="roleID"
          isBusy={isLoading}
          columns={roleColumns}
          dataItems={data?.data || []}
          // queryConfig={rolesApiKeyStore.getRoles()}
          // onEdit={handleEdit}
          customCell={(field) =>
            CustomGridCell({
              field,
            })
          }
          actionPermissions={{
            export: UserPermissionName.DOWNLOAD_GRID,
          }}
        // deleteMutationConfig={(deleteItem) => rolesApiKeyStore.deleteRole(deleteItem.roleID)}
        // actionPermissions={{
        //   edit: UserPermissionName.EDIT_MASTER,
        //   delete: UserPermissionName.DELETE_MASTER,
        // }}
        />
        {editItem && (
          <SaveHookFormDialog
            title={editItem.roleID ? 'UPDATE ROLE' : 'CREATE ROLE'}
            config={{
              saveConfig: editItem.roleID ? rolesApiKeyStore.updateRole() : rolesApiKeyStore.createRole(),
              invalidateRoute: () => rolesApiKeyStore.getRoles(),
            }}
            data={{ ...editItem, companyID: 1, status: 'Active' }}
            onClose={handleEditClose}
            size="md"
            formatSubmitdata={formatSubmitdata}
            yupSchema={roleFormSchema}
          >
            <RoleForm permissionList={permissionList?.data ?? []} editItem={editItem} />
          </SaveHookFormDialog>
        )}
        {isOpen && (
          <Dialog className="modalpopup" title={'CONFIRM DELETE'} onClose={closeDialog}>
            <div>
              <div style={{ padding: '25px 50px' }}>
                <div>Are you sure you want to permanently delete?</div>
                <LoadingIndicator isLoading={isLoading} />
                {/* <ApiErrorComponent isError={isError} errorRespnse={error} /> */}
              </div>
            </div>
            <DialogActionsBar>
              <div className=" spaceBetween">
                <button className="btn btn-sm btn-default btn-lightGrey" onClick={closeDialog} autoFocus>
                  Cancel
                </button>
                <button
                  className="btn btn-sm btn-danger btn-delete"
                  style={{
                    backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
                    color: isReadOnlyPermission ? 'black' : '#fff'
                  }}
                  type="button"
                  onClick={submitHanlder}
                  disabled={isLoading}
                >
                  Delete
                </button>
              </div>
            </DialogActionsBar>
          </Dialog>
        )}
      </Paper>
    </SiTrackPage>
  );
};
